import { type FC } from 'react';
import { IconTrash } from '@tabler/icons-react';
import { formatDistanceToNow } from 'date-fns';

import type { OngoingEvaluation } from '@/types';
import { ButtonField } from '@wk/components-react16';
import { Link } from 'react-router';
import {
  getOngoingEvaluationError,
  ongoingEvaluationSupportMessage,
} from '@/lib/ongoingEvaluationError';

interface OngoingEvaluationItemProps {
  evaluation: OngoingEvaluation;
  onDelete: (jobId: string) => void;
  onViewResults: () => void;
}

const progressBarColors = {
  Failed: 'bg-red-500',
  TimedOut: 'bg-red-500',
  Completed: 'bg-green-500',
  Running: 'bg-blue-500',
  Pending: 'bg-blue-500',
};

const OngoingEvaluationItem: FC<OngoingEvaluationItemProps> = ({
  evaluation,
  onDelete,
  onViewResults,
}) => {
  const isIndeterminate = ['Running', 'Pending'].includes(evaluation.status);
  const recordId = evaluation.output?.recordId;
  const errorMessage =
    evaluation.status === 'Failed' || evaluation.status === 'TimedOut'
      ? getOngoingEvaluationError(
          evaluation.message ?? evaluation.output,
          evaluation.status,
        )
      : null;

  return (
    <div className="bg-white border border-gray-200 py-2 px-4 mb-4 relative min-w-0 max-w-full [overflow-wrap:anywhere]">
      {/* Delete Icon */}
      <button
        onClick={() => onDelete(evaluation.jobId)}
        className="absolute top-3 right-3 text-gray-400 hover:text-red-500 cursor-pointer transition-colors"
        aria-label="Delete evaluation"
      >
        <IconTrash size={16} />
      </button>

      {/* Service Name */}
      <h3 className="text-sm font-semibold text-gray-900 mb-1 pr-6">
        {evaluation.applicationName}
      </h3>

      {/* ID */}
      <p className="text-xs text-gray-600 mb-1">{evaluation.bitId}</p>

      {/* Job ID */}
      <p className="text-xs text-gray-600 mb-3 font-medium">
        Job ID: {evaluation.jobId}
      </p>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 h-1 mb-3 overflow-hidden">
        {isIndeterminate ? (
          <div
            className="h-1 bg-[#005b92]"
            style={{
              width: '30%',
              transform: 'translateX(-100%)',
              animation: 'progressBarLoading 1.8s ease-in-out infinite',
            }}
          />
        ) : (
          <div
            className={`h-1 w-full ${progressBarColors[evaluation.status]}`}
          />
        )}
      </div>

      {errorMessage && (
        <p role="alert" className="mb-2 text-xs text-red-700">
          <span className="block">
            {errorMessage.replace(` ${ongoingEvaluationSupportMessage}`, '')}
          </span>
          <span className="mt-2 block">{ongoingEvaluationSupportMessage}</span>
        </p>
      )}
      <div className="flex flex-wrap justify-between items-center gap-2">
        {evaluation.status === 'Completed' && recordId && (
          <ButtonField mode="text">
            <Link
              onClick={() => {
                onDelete(evaluation.jobId);
                onViewResults();
              }}
              to={`/evaluations/${recordId}`}
              className=""
            >
              View Results
            </Link>
          </ButtonField>
        )}
        {(evaluation.status === 'Failed' || evaluation.status === 'TimedOut') && (
          <button
            type="button"
            className="text-xs text-red-700 underline cursor-pointer"
            onClick={() => onDelete(evaluation.jobId)}
          >
            Clear
          </button>
        )}
        <span className="text-xs text-gray-500 ml-auto">
          {formatDistanceToNow(new Date(evaluation.startedAt), {
            addSuffix: true,
          })}
        </span>
      </div>
    </div>
  );
};

export default OngoingEvaluationItem;
